/**
 * Minimum time to show loading (allows animation to play smoothly).
 */
export const MIN_LOADING_TIME = 300; // ms

/**
 * Run `fn` after the browser has painted the current frame.
 *
 * A bare setTimeout(0) usually fires BEFORE the next paint, so the heavy
 * tab-switch render would block the loading placeholder from ever appearing —
 * the click felt dead until the whole new tab finished rendering. rAF fires
 * just before paint; the nested setTimeout(0) queues `fn` right after it.
 * The fallback timer covers throttled rAF (e.g. hidden/occluded window).
 */
export function runAfterPaint(fn) {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    fn();
  };
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => setTimeout(run, 0));
    setTimeout(run, 150);
  } else {
    setTimeout(run, 0);
  }
}

/**
 * When selecting a campaign without an explicit `options.view`, preserve the active tab's
 * view *type* (flow/kanban/…) so sidebar/nested nav doesn't snap back to the global default.
 *
 * customViewId is campaign-scoped — only keep it when staying on the same campaign.
 * Carrying it across campaigns leaves a stale `cv` in the URL/tab and makes
 * selectViewWithUrlSync fall back after "view not found".
 */
export function mergeViewIntoSprintOptions(options, activeTab, preferences, targetId = null) {
  const defaultVm = preferences?.defaultView ?? 'flow';
  if (options.view !== undefined && options.view !== null) {
    return { ...options };
  }
  if (!activeTab || activeTab.type === 'blank') {
    return { ...options, view: defaultVm };
  }
  const vm = activeTab.viewMode ?? defaultVm;
  const sameCampaign =
    activeTab.type === 'campaign' &&
    targetId != null &&
    activeTab.targetId === targetId;
  const cv = sameCampaign ? activeTab.customViewId : null;
  if (cv) {
    return { ...options, view: { customViewId: cv, type: vm } };
  }
  return { ...options, view: vm };
}

export const isTemplateItem = (item) =>
  item?.isTemplate === true || item?.type === 'template';

/**
 * Wait for data load (and min display time when switching to new content),
 * then clear loading — unless a newer navigation superseded this one.
 */
export async function finishTabLoad({
  seq,
  tabNavigationSeqRef,
  isNew,
  loadingPromise,
  setIsLoadingItem,
}) {
  const minTimePromise = new Promise((resolve) => setTimeout(resolve, MIN_LOADING_TIME));

  try {
    if (isNew) {
      await Promise.all([loadingPromise, minTimePromise]);
    } else {
      await loadingPromise;
    }
  } finally {
    // A rejected load must still drop the flag. A newer click owns it instead.
    if (seq === tabNavigationSeqRef.current) {
      setIsLoadingItem?.(false);
    }
  }
}

/**
 * Clear Mission Full View + open doc sheet state on a tab.
 * Same payload as useTabMissionOverlay.closeMission.
 */
export function clearedMissionOverlayFields() {
  return {
    openMissionId: null,
    missionDocStack: [],
    missionDocScrollById: {},
    missionDocDirect: false,
  };
}

/**
 * Build tab field updates when navigating to a campaign within the current tab.
 */
export function buildCampaignTabUpdates(activeTab, sprint, view, preferences) {
  const isNewContent = activeTab.targetId !== sprint.id || activeTab.type !== 'campaign';
  const tabUpdates = {
    type: 'campaign',
    targetId: sprint.id,
    label: sprint.name,
    icon: sprint.icon,
  };

  if (typeof view === 'object' && view?.customViewId) {
    // Explicit / same-campaign view id from mergeViewIntoSprintOptions or URL
    tabUpdates.viewMode = view.type || activeTab.viewMode;
    tabUpdates.customViewId = view.customViewId;
  } else if (typeof view === 'object' && view?.type) {
    tabUpdates.viewMode = view.type;
    tabUpdates.customViewId = null;
  } else if (typeof view === 'string') {
    tabUpdates.viewMode = view;
    tabUpdates.customViewId = null;
  } else {
    tabUpdates.viewMode = activeTab.viewMode || preferences.defaultView || 'flow';
    tabUpdates.customViewId = isNewContent ? null : (activeTab.customViewId ?? null);
  }

  // Switching campaigns in-tab must tear down MFV + any open doc — otherwise
  // the overlay stays mounted over the new campaign's content.
  if (isNewContent) {
    Object.assign(tabUpdates, clearedMissionOverlayFields());
  }

  return { tabUpdates, isNewContent };
}
